import { format, parse } from "date-fns";

const getServerUrl = () => {
  return `${window.location.protocol}//${window.location.host}`;
};
export const server = getServerUrl();

// Error messages for authentication
const authErrorMessages = {
  400: "Invalid email or password format",
  401: "Incorrect email or password",
  403: "Your account is not authorized to access this system",
  404: "Server not found. Check your internet connection, and contact SAMI support if the issue persists.",
  429: "Too many login attempts. Please try again later",
  500: "Server error. Please try again later",
  502: "Server is temporarily unavailable. Please try again later",
  503: "Service unavailable. Please try again later",
  // Default message for unknown error codes
  default: "Unable to connect. Please check your internet connection, and contact SAMI support if the issue persists."
};

// LOGIN
export async function auth(accountname, password) {
  let errorMessage = "";
  try {
    const response = await fetch(`${server}/authentication`, {
      method: 'POST',
      headers: {
        'Connection': 'keep-alive',
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        strategy: 'local',
        email: accountname,
        password: password
      })
    });
    
    if (!response.ok) {
      // Get the user-friendly message or fall back to default
      
      if (response.status == 500) {
        const body = await response.json();
        errorMessage = body.message;
      }
      if (!errorMessage) {
        errorMessage = authErrorMessages[response.status] || authErrorMessages.default;
      }
      return {
        ok: false,
        statusText: errorMessage,
        status: response.status
      };
    } else {
      const body = await response.json();
      const { accessToken } = body;
      if (accessToken) {
        sessionStorage.setItem('token', accessToken);
      }
      return { ...body, ok: true };
    }
  } catch (error) {
    return {
      ok: false,
      statusText: authErrorMessages.default,
      status: 'network_error'
    };
  }
}

// GET USER DETAILS
export async function getUserDetails() {
  try {
    const response = await fetch(`${server}/fmpeople/`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${sessionStorage.token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const body = await response.json();
    return body;
  } catch (error) {
    return error.message;
  }
}

//GET ORDERS NEW
export async function getOrdersAPI(
  limit,
  searchString,
  offset,
  statusArray,
  dateRange,
  dateField,
  sortBy,
  companyId
) {

  const queryParams = new URLSearchParams({
    limit,
    searchString,
    offset,
    dateRange,
    dateField,
    sortBy,
    companyId,
  });

  // Manually append each element of the statusArray
  if (Array.isArray(statusArray)) {
    statusArray.forEach((status) =>
      queryParams.append("statusArray", status)
    );
  }

  const response = await fetch(
    `${server}/fmorders?${queryParams.toString()}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${sessionStorage.token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }
  );

  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`);
    error.status = response.status;
    throw error;
  }

  const body = await response.json();
  return body;
}

// CREATE ORDER
export async function createOrder(data) {
  try {
    const response = await fetch(`${server}/fmorders`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${sessionStorage.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const body = await response.json();
    return body;
  } catch (error) {
    return error.message;
  }
}

// UPDATE ORDER
export async function updateOrder(recordId, data) {
  try {
    const response = await fetch(`${server}/fmorders/${recordId}`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${sessionStorage.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const body = await response.json();
    return body;
  } catch (error) {
    return error.message;
  }
}

// CREATE DA PDF
// perform script to create the DA PDF on FileMaker Server
export async function createDADownload(deliveryId) {
  const body = {
    deliveryId: deliveryId,
  };

  try {
    const response = await fetch(`${server}/fmdownloads`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${sessionStorage.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseBody = await response.json();
    return responseBody;
  } catch (error) {
    return error.message;
  }
}

export async function createOrderDownloadSDS(orderId) {
  const body = {
    orderId: orderId,
  };

  try {
    const response = await fetch(`${server}/fmdownloadssds`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${sessionStorage.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseBody = await response.json();
    return responseBody;
  } catch (error) {
    return error.message;
  }
}

export async function postLogEvent(orderNum, eventName) {
  const body = {
    orderNum: orderNum,
    eventName: eventName
  };

  try {
    const response = await fetch(`${server}/fmlogevent`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${sessionStorage.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseBody = await response.json();
    return responseBody;
  } catch (error) {
    return error.message;
  }
}

// DOWNLOAD WEB DOWNLOAD
export async function getWebDownload(recordId, filename) {
  try {
    const response = await fetch(
      `${server}/fmdownloads/${recordId}?filename=${filename}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${sessionStorage.token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const body = await response.json();
    
    // Check if body is an error object
    if (body.name && body.message && body.code) {
      const error = new Error(body.message);
      error.name = body.name;
      error.code = body.code;
      throw error;
    }
    
    // Return the URL (either as string or object with url property)
    return typeof body === 'string' ? `${server}${body}` : `${server}${body.url || body}`;
  } catch (error) {
    throw error;
  }
}

//CREATE LAB REPORT PDF
//perform script to create the PDF on FileMaker Server
export function createLabDownload(deliveryId) {
  const body = {
    deliveryId: deliveryId,
  };

  return fetch(`${server}/fmlabreports`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${sessionStorage.token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  })
    .then((response) => {
      if (!response.ok) {
        return response.json().then((error) => {
          throw new Error(error);
        });
      }
      return response.json();
    })
    .then((data) => {
      return data;
    })
    .catch((error) => {
      return error.message;
    });
}

// LOGOUT
export function logout() {
  return fetch(`${server}/authentication`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${sessionStorage.token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  })
    .then((response) => {
      if (!response.ok) {
        return response.json().then((error) => {
          throw new Error(error);
        });
      }
      return response.json();
    })
    .then((data) => {
      sessionStorage.clear();
      return data;
    })
    .catch((error) => {
      return error.message;
    });
}

//EXPORT ORDERS LIST TO CSV
export async function ordersToCSV(
  searchString,
  offset,
  limit,
  statusArray,
  dateRange,
  dateField,
  sortBy,
  companyId
) {
  //get a set of orders from the API...
  let response;
  try {
    const queryParams = new URLSearchParams({
      limit,
      searchString,
      offset,
      dateRange,
      dateField,
      sortBy,
      companyId,
    });
    
    // Manually append each element of the statusArray
    if (Array.isArray(statusArray)) {
      statusArray.forEach((status) =>
        queryParams.append("statusArray", status)
      );
    }
    response = await fetch(
      `${server}/fmorders?${queryParams.toString()}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${sessionStorage.token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error);
    }

    response = await response.json();
  } catch (error) {
    console.error(error);
    throw new Error("Can't retrive orders. Limit = ${limit}");
  }

  let orders = [];
  if (!response.messages) {
    orders = response;
  }
  const names = [
    "Date Ordered",
    "SAMI Invoice Number",
    "Product",
    "Batch Numbers",
    "Customer Order Number",
    "SBT Order Number",
    "SAMI Docket Number",
    "Final Volume lts",
    "Final Weight kg",
    "Additive Total kg",
    "Qty Flush Kero",
    "Carrier",
    "Date Loaded",
    "Status",
    "Depot",
    "Amount",
    "UOM",
    "Lab Reports Numbers",
  ];

  //create an array of each row, comma seperated
  let csv = orders.map((order) =>
    [
      format(
        parse(order.fieldData.Date_Order, "MM/dd/yyyy", new Date()),
        "dd/MM/yyyy"
      ),
      // order.fieldData.Date_Order,
      order.fieldData["Order_DELIVERY_delete::SAMI_Invoice_Number"],
      order.portalData.Order_ORDERLINE_web_create[0] &&
        order.portalData.Order_ORDERLINE_web_create[0][
          "Order_ORDERLINE_web_create::ItemName_inc_Variant_c"
        ],
      order.fieldData["Order_DELIVERY_delete::Batch_Numbers_for_CSV_c"],
      order.fieldData.Order_Number_Customer.replace(
        /(?:\r\n|\r|\n)/g,
        " "
      ).trim(),
      new String(order.fieldData.SBT_Order_Number)
        .trim()
        .replace(/(?:\r\n|\r|\n)/g, " "),
      order.fieldData["Order_DELIVERY_delete::SAMI_Docket_Number"],
      order.fieldData[
        "Order_Delivery_DELIVERYLINE::Final_Weight_Product_andCutter_c"
      ],
      order.fieldData["Order_Delivery_DELIVERYLINE::Final_Weight_Total_Load_c"],
      order.fieldData["Order_Delivery_DELIVERYLINE::Additive_KG_Total"],
      order.fieldData["Order_Delivery_DELIVERYLINE::Qty_Flush_Kero"],
      order.fieldData["Order_CARRIERJOIN_depot_load::Carrier_Name_ae"],
      order.fieldData.Date_Loaded_ae
        ? format(
            parse(order.fieldData.Date_Loaded_ae, "MM/dd/yyyy", new Date()),
            "dd/MM/yyyy"
          )
        : order.fieldData.Date_Loaded_ae,
      order.fieldData.Status_c,
      order.fieldData["Order_LOCATION::Name"],
      order.portalData.Order_ORDERLINE_web_create[0] &&
        order.portalData.Order_ORDERLINE_web_create[0][
          "Order_ORDERLINE_web_create::Qty_Ordered"
        ],
      order.portalData.Order_ORDERLINE_web_create[0] &&
        order.portalData.Order_ORDERLINE_web_create[0][
          "Order_ORDERLINE_web_create::Unit_of_Measure_Short_ordered_ae"
        ]?.replace(/kg/gi, "kg")
          .replace(/l/g, "L"),
      order.fieldData["Order_DELIVERY_delete::Lab_Report_Number_List_c"],
    ].join(",")
  );

  //Add the names to the top
  csv.unshift(names.join(","));
  csv = csv.join("\r\n");
  textToFile(csv, "SAMI Order Export.csv");

  //export csv to file
  function textToFile(text, filename) {
    const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
    
    if (navigator.msSaveBlob) {
      // IE 10+
      navigator.msSaveBlob(blob, filename);
    } else {
      const link = document.createElement("a");
      if (link.download !== undefined) {
        // Browsers that support HTML5 download attribute
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url); // Clean up
      } else {
        console.error("Download attribute is not supported in this browser.");
      }
    }
  }
}